// src/pages/HomeFeedNews.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { User, LogOut } from 'lucide-react';
import {
  Heart,
  MessageCircle,
  Share2,
  Wrench,
  Briefcase,
  Users,
  Settings,
  Bookmark,
  Bell,
  TrendingUp,
  Clock,
  Search,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SearchDrawer from '../components/SearchDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// small helpers
const safePublic = (p) => `${process.env.PUBLIC_URL || ''}${p}`;
const isHttp = (s) => typeof s === 'string' && /^https?:\/\//i.test(s);

const shuffleWithSeed = (arr, seed) => {
  // deterministic shuffle (so "refresh" changes when seed changes)
  const a = [...arr];
  let x = seed || 1;
  const rand = () => {
    x = (x * 1664525 + 1013904223) % 4294967296;
    return x / 4294967296;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const HomeFeedNews = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('feed');

  const [posts, setPosts] = useState([]);
  const [news, setNews] = useState([]);
  const [ads, setAds] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [closingSoonJobs, setClosingSoonJobs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);

  const observerTarget = useRef(null);

  const [composerText, setComposerText] = useState('');
  const [postType, setPostType] = useState('PERSONAL');
  const [posting, setPosting] = useState(false);

  const [mediaImages, setMediaImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});

  const [showSearch, setShowSearch] = useState(false);

  // ✅ avatar dropdown (click + click outside)
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);

  // one-per-mount seed (refresh => new seed)
  const seedRef = useRef(Date.now());

  useEffect(() => {
    const onDocClick = (e) => {
      if (!accountRef.current) return;
      if (!accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // close dropdown on route-ish changes (tab switch)
  useEffect(() => {
    setAccountOpen(false);
  }, [activeTab]);

  const fetchPosts = useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const currentSkip = reset ? 0 : skip;
        // cache-buster to avoid any unwanted caching
        const response = await axios.get(
          `${API}/feed/posts?skip=${currentSkip}&limit=20&t=${Date.now()}`
        );

        const newPosts = response.data.posts || [];

        if (reset) {
          setPosts(newPosts);
          setSkip(20);
        } else {
          setPosts((prev) => [...prev, ...newPosts]);
          setSkip((prev) => prev + 20);
        }

        setHasMore(newPosts.length === 20);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [skip]
  );

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/news?limit=50&t=${Date.now()}`);
      setNews(response.data.news || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feed/ads?t=${Date.now()}`);
      setAds(response.data.ads || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchFeaturedListings = useCallback(async () => {
    try {
      const roleContext = user ? 'personal' : 'public';
      const response = await axios.get(
        `${API}/home/featured?role_context=${roleContext}&limit=6&t=${Date.now()}`
      );

      const items = response.data.items || [];
      // rotate/shuffle so refresh looks fresh (doesn't break correctness)
      const shuffled = shuffleWithSeed(items, seedRef.current);
      setFeaturedListings(shuffled.slice(0, 6));
    } catch (error) {
      console.error(error);
    }
  }, [user]);

  const fetchClosingSoon = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/saved/closing-soon?t=${Date.now()}`);
      setClosingSoonJobs(response.data.jobs || []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'feed') {
      fetchPosts(true);
      fetchNews();
    } else if (activeTab === 'posts') {
      fetchPosts(true);
    } else {
      fetchNews();
    }

    fetchAds();
    fetchFeaturedListings();
    if (user) fetchClosingSoon();
  }, [activeTab, user, fetchPosts, fetchNews, fetchAds, fetchFeaturedListings, fetchClosingSoon]);

  // infinite scroll (feed only)
  useEffect(() => {
    const el = observerTarget.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && activeTab === 'feed') {
          fetchPosts(false);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, activeTab, fetchPosts]);

  const handleCreatePost = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!composerText.trim() && mediaImages.length === 0) {
      toast.error('Please write something or attach media');
      return;
    }
    setPosting(true);
    try {
      await axios.post(`${API}/feed/posts`, {
        author_mode: 'PERSONAL',
        post_type: postType,
        text: composerText,
        media_images: mediaImages,
      });
      toast.success('Posted!');
      setComposerText('');
      setMediaImages([]);
      await fetchPosts(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - mediaImages.length);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/media/upload-image`, formData);
        return response.data.url;
      });
      const urls = await Promise.all(uploadPromises);
      setMediaImages((prev) => [...prev, ...urls]);
      toast.success('Images uploaded');
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setMediaImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLikeNews = async (newsId) => {
    if (!user) {
      toast.error('Please login to like');
      navigate('/login');
      return;
    }

    const newsItem = news.find((n) => n.id === newsId);
    const wasLiked = newsItem?.viewer_has_liked;

    setNews((prev) =>
      prev.map((n) =>
        n.id === newsId
          ? {
              ...n,
              viewer_has_liked: !wasLiked,
              like_count: wasLiked ? (n.like_count || 0) - 1 : (n.like_count || 0) + 1,
            }
          : n
      )
    );

    try {
      const response = await axios.post(`${API}/news/${newsId}/like`);
      setNews((prev) =>
        prev.map((n) =>
          n.id === newsId
            ? { ...n, viewer_has_liked: response.data.liked, like_count: response.data.like_count }
            : n
        )
      );
    } catch (error) {
      setNews((prev) =>
        prev.map((n) =>
          n.id === newsId
            ? {
                ...n,
                viewer_has_liked: wasLiked,
                like_count: wasLiked ? (n.like_count || 0) + 1 : (n.like_count || 0) - 1,
              }
            : n
        )
      );
      toast.error('Failed to update like');
    }
  };

  const handleLikePost = async (postId) => {
    if (!user) {
      toast.error('Please login to like');
      navigate('/login');
      return;
    }

    const post = posts.find((p) => p.id === postId);
    const wasLiked = post?.viewer_has_liked;

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              viewer_has_liked: !wasLiked,
              like_count: wasLiked ? (p.like_count || 0) - 1 : (p.like_count || 0) + 1,
            }
          : p
      )
    );

    try {
      const response = await axios.post(`${API}/feed/posts/${postId}/like`);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, viewer_has_liked: response.data.liked, like_count: response.data.like_count } : p
        )
      );
    } catch (error) {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                viewer_has_liked: wasLiked,
                like_count: wasLiked ? (p.like_count || 0) + 1 : (p.like_count || 0) - 1,
              }
            : p
        )
      );
      toast.error('Failed to update like');
    }
  };

  const toggleComments = async (postId) => {
    if (!showComments[postId]) {
      try {
        const response = await axios.get(`${API}/feed/posts/${postId}/comments`);
        setComments((prev) => ({ ...prev, [postId]: response.data.comments || [] }));
      } catch (error) {
        console.error(error);
      }
    }
    setShowComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleAddComment = async (postId) => {
    if (!user) {
      toast.error('Please login to comment');
      navigate('/login');
      return;
    }
    if (!commentText[postId]?.trim()) return;

    try {
      await axios.post(
        `${API}/feed/posts/${postId}/comments?text=${encodeURIComponent(commentText[postId])}`
      );
      setCommentText((prev) => ({ ...prev, [postId]: '' }));
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p))
      );
      const response = await axios.get(`${API}/feed/posts/${postId}/comments`);
      setComments((prev) => ({ ...prev, [postId]: response.data.comments || [] }));
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const pinnedNews = useMemo(() => news.filter((n) => n.pinned), [news]);
  const regularNews = useMemo(() => news.filter((n) => !n.pinned), [news]);

  // ✅ Build stream without setState in render
  const mixedStream = useMemo(() => {
    if (activeTab !== 'feed') return [];

    const stream = [];
    let adCount = 0;

    // rotate news start index so refresh feels different
    const newsStart = regularNews.length ? seedRef.current % regularNews.length : 0;
    let injectedNewsCount = 0;

    posts.forEach((post, idx) => {
      // post
      stream.push({ type: 'post', data: post, idx });

      // carousel every 10 posts (after the first)
      const shouldShowCarousel = idx > 0 && idx % 10 === 0 && featuredListings.length > 0;
      if (shouldShowCarousel) {
        stream.push({ type: 'carousel' });
      }

      // ad every 6 posts (but not on the same slot as carousel), max 3
      const shouldShowAd = idx > 0 && idx % 6 === 0 && idx % 10 !== 0 && adCount < 3 && ads.length > 0;
      if (shouldShowAd) {
        const adIdx = (seedRef.current + adCount) % ads.length;
        stream.push({ type: 'ad', data: ads[adIdx] });
        adCount += 1;
      }

      // inject news every 8 posts
      if ((idx + 1) % 8 === 0 && regularNews.length > 0) {
        const newsIdx = (newsStart + injectedNewsCount) % regularNews.length;
        stream.push({ type: 'news', data: regularNews[newsIdx] });
        injectedNewsCount += 1;
      }
    });

    return stream;
  }, [activeTab, posts, regularNews, ads, featuredListings]);

  const handleListingClick = (listing) => {
    const url = listing?.deeplinkUrl;
    if (!url) return;
    if (isHttp(url)) window.location.href = url;
    else navigate(url);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Full-width header (no max-w frame) */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="w-full px-3 sm:px-4 lg:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 sm:gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
              aria-label="GoFella Home"
            >
              <img src="/assets/slogocolor.png" alt="GoFella" className="h-7 w-auto dark:hidden" />
              <img src="/assets/slogowhite.png" alt="GoFella" className="hidden h-7 w-auto dark:block" />
            </button>

            <div className="hidden md:flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/tradies/browse')}
                className="hover:bg-primary/10 hover:text-primary"
              >
                <Wrench className="w-4 h-4 mr-1" />
                Tradies
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/jobs/browse')}
                className="hover:bg-primary/10 hover:text-primary"
              >
                <Briefcase className="w-4 h-4 mr-1" />
                Jobs
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/portal/talent')}
                className="hover:bg-primary/10 hover:text-primary"
              >
                <Users className="w-4 h-4 mr-1" />
                Talent
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowSearch(true)}
              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
              aria-label="Search"
            >
              <Search className="w-5 h-5 text-foreground" />
            </button>

            <ThemeToggle />

            {user ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/messages')} aria-label="Notifications">
                  <Bell className="w-4 h-4" />
                </Button>

                {/* ✅ click-based dropdown */}
                <div className="relative" ref={accountRef}>
                  <button
                    onClick={() => setAccountOpen((v) => !v)}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold overflow-hidden"
                    aria-label="Account menu"
                  >
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        className="w-full h-full object-cover"
                        alt={user.full_name}
                      />
                    ) : (
                      (user.full_name || 'U')[0]
                    )}
                  </button>

                  {accountOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-card border rounded-lg shadow-lg p-2 z-50">
                      <button
                        onClick={() => {
                          setAccountOpen(false);
                          navigate('/profile');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted rounded"
                      >
                        <span className="inline-flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Profile
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setAccountOpen(false);
                          navigate('/settings');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted rounded"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Settings
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setAccountOpen(false);
                          logout();
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted rounded text-red-600"
                      >
                        <span className="inline-flex items-center gap-2">
                          <LogOut className="w-4 h-4" />
                          Logout
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  Login
                </Button>
                <Button onClick={() => navigate('/register')}>Sign Up</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ✅ Full-width content wrapper (no max-w frame) */}
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* ✅ Responsive layout:
            - mobile: 1 column
            - tablet (md): 2 columns (left + feed), right goes full width below
            - pc (lg+): 15% - 60% - 25%
        */}
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] lg:grid-cols-[15%_60%_25%] gap-4 lg:gap-6 items-start">
          {/* Center Feed (first on mobile) */}
          <div className="order-1 md:order-2">
            {/* Tabs */}
            <div className="flex gap-2 border-b mb-4">
              <button
                onClick={() => setActiveTab('feed')}
                className={`px-4 py-2 font-semibold ${
                  activeTab === 'feed' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-muted-foreground'
                }`}
              >
                For You
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-4 py-2 font-semibold ${
                  activeTab === 'posts' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-muted-foreground'
                }`}
              >
                Posts
              </button>
              <button
                onClick={() => setActiveTab('news')}
                className={`px-4 py-2 font-semibold ${
                  activeTab === 'news' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-muted-foreground'
                }`}
              >
                News
              </button>
            </div>

            {/* FEED */}
            {activeTab === 'feed' && (
              <>
                {/* Composer */}
                {user ? (
                  <Card className="mb-4">
                    <CardContent className="p-4">
                      <Textarea
                        placeholder="What's on your mind?"
                        value={composerText}
                        onChange={(e) => setComposerText(e.target.value)}
                        rows={3}
                        className="mb-3"
                      />

                      {/* Media Preview */}
                      {mediaImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {mediaImages.map((img, idx) => (
                            <div key={idx} className="relative">
                              <img src={img} className="w-full h-20 object-cover rounded" alt="" />
                              <button
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1"
                                aria-label="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            value={postType}
                            onChange={(e) => setPostType(e.target.value)}
                            className="
                              px-3 py-1 rounded text-sm
                              border border-border
                              bg-background text-foreground
                              focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                            "
                          >
                            <option value="PERSONAL">Personal</option>
                            <option value="WORK">Work</option>
                            <option value="HIRING">Hiring</option>
                            <option value="PROMOTION">Promotion</option>
                          </select>

                          {mediaImages.length < 4 && (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                                id="image-upload-feed"
                                disabled={uploading}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={uploading}
                                onClick={() => document.getElementById('image-upload-feed')?.click()}
                              >
                                <ImageIcon className="w-4 h-4 mr-1" />
                                {uploading ? 'Uploading...' : 'Add Photos'}
                              </Button>
                            </>
                          )}
                        </div>

                        <Button onClick={handleCreatePost} disabled={posting || uploading} size="sm">
                          {posting ? 'Posting...' : 'Post'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="mb-4">
                    <CardContent className="p-6 text-center">
                      <p className="text-muted-foreground mb-3">Join GoFella to share your story</p>
                      <Button onClick={() => navigate('/login')}>Login to Post</Button>
                    </CardContent>
                  </Card>
                )}

                {/* Mixed Feed */}
                <div className="space-y-4">
                  {mixedStream.map((item, streamIdx) => {
                    if (item.type === 'carousel') {
                      return (
                        <Card key={`carousel-${streamIdx}`} className="border border-border bg-muted/30">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-muted-foreground">Featured Listings</span>
                            </div>

                            <div
                              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
                              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                              {featuredListings.map((listing) => (
                                <div
                                  key={listing.id}
                                  className="min-w-[200px] max-w-[200px] snap-start cursor-pointer flex-shrink-0"
                                  onClick={() => handleListingClick(listing)}
                                >
                                  <div className="border rounded-lg p-3 hover:shadow-lg transition-shadow bg-card h-full flex flex-col">
                                    <div className="w-full h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded mb-2 flex items-center justify-center overflow-hidden">
                                      {listing.imageUrl ? (
                                        <img
                                          src={listing.imageUrl}
                                          onError={(e) => {
                                            e.currentTarget.src = safePublic('/demo/placeholder.jpg');
                                          }}
                                          className="w-full h-full object-cover"
                                          alt={listing.title}
                                          loading="lazy"
                                        />
                                      ) : (
                                        <span className="text-white text-xs font-semibold">{listing.type}</span>
                                      )}
                                    </div>

                                    <h4 className="font-semibold text-sm line-clamp-2 mb-1 min-h-[2.5rem] flex-grow">
                                      {listing.title}
                                    </h4>
                                    <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{listing.subtitle}</p>

                                    {listing.type === 'JOBS' && (
                                      <div className="space-y-1 mb-2">
                                        {listing.location && (
                                          <p className="text-xs text-muted-foreground">📍 {listing.location}</p>
                                        )}
                                        {listing.salary && (
                                          <p className="text-xs font-semibold text-green-600">{listing.salary}</p>
                                        )}
                                      </div>
                                    )}

                                    {listing.badgeText && (
                                      <Badge variant="outline" className="text-xs w-fit">
                                        {listing.badgeText}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    if (item.type === 'ad') {
                      const ad = item.data;
                      return (
                        <Card key={`ad-${streamIdx}`} className="border-2 border-blue-200">
                          <CardContent className="p-4">
                            <Badge variant="outline" className="text-xs mb-2">
                              Sponsored
                            </Badge>
                            <h3 className="font-bold mb-1">{ad.title}</h3>
                            <p className="text-sm text-muted-foreground mb-3">{ad.subtitle}</p>
                            <div className="flex items-center justify-between gap-2">
                              <Button size="sm" onClick={() => (ad.cta_url ? navigate(ad.cta_url) : null)}>
                                {ad.cta_text || 'Learn more'}
                              </Button>
                              <button onClick={handleShare} className="text-sm text-muted-foreground hover:text-foreground">
                                <span className="inline-flex items-center gap-1">
                                  <Share2 className="w-4 h-4" />
                                  Share
                                </span>
                              </button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    if (item.type === 'news') {
                      const n = item.data;
                      return (
                        <Card key={`news-${n.id}-${streamIdx}`} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <Badge className="mb-2 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                              GoNews
                            </Badge>
                            <h3 className="font-bold text-lg mb-1">{n.title}</h3>
                            <p className="text-sm text-muted-foreground mb-2">
                              {new Date(n.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-foreground mb-3 whitespace-pre-wrap">{n.body}</p>
                            <button
                              onClick={() => handleLikeNews(n.id)}
                              className={`flex items-center gap-1 ${
                                n.viewer_has_liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                              }`}
                            >
                              <Heart className={`w-4 h-4 ${n.viewer_has_liked ? 'fill-red-500' : ''}`} />
                              <span className="text-sm font-medium">{n.like_count || 0}</span>
                            </button>
                          </CardContent>
                        </Card>
                      );
                    }

                    // post
                    const post = item.data;
                    return (
                      <Card key={`post-${post.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold overflow-hidden">
                              {post.author?.avatar_url ? (
                                <img
                                  src={post.author.avatar_url}
                                  className="w-full h-full object-cover"
                                  alt={post.author?.full_name}
                                />
                              ) : (
                                post.author?.full_name?.[0] || 'U'
                              )}
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => navigate(`/users/${post.author?.id || post.author_user_id}`)}
                                  className="font-semibold hover:underline"
                                >
                                  {post.author?.full_name || 'User'}
                                </button>
                                <Badge variant="outline" className="text-xs">
                                  {post.post_type}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {new Date(post.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>

                          <p className="text-foreground mb-3 whitespace-pre-wrap">{post.text}</p>

                          {post.media_images?.length > 0 && (
                            <div className={`grid ${post.media_images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-2 mb-3`}>
                              {post.media_images.slice(0, 4).map((img, imgIdx) => (
                                <img
                                  key={imgIdx}
                                  src={isHttp(img) ? img : safePublic(img)}
                                  onError={(e) => {
                                    e.currentTarget.src = safePublic('/demo/placeholder.jpg');
                                  }}
                                  className="w-full h-40 object-cover rounded"
                                  alt=""
                                  loading="lazy"
                                />
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-4 pt-3 border-t">
                            <button
                              onClick={() => handleLikePost(post.id)}
                              className={`flex items-center gap-1 transition-colors ${
                                post.viewer_has_liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                              }`}
                            >
                              <Heart className={`w-4 h-4 ${post.viewer_has_liked ? 'fill-red-500' : ''}`} />
                              <span className="text-sm font-medium">{post.like_count || 0}</span>
                            </button>

                            <button
                              onClick={() => toggleComments(post.id)}
                              className="flex items-center gap-1 text-muted-foreground hover:text-blue-500"
                            >
                              <MessageCircle className="w-4 h-4" />
                              <span className="text-sm">{post.comment_count || 0}</span>
                            </button>
                          </div>

                          {showComments[post.id] && (
                            <div className="mt-3 pt-3 border-t space-y-3">
                              {comments[post.id]?.map((comment) => (
                                <div key={comment.id} className="flex gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                    {comment.author?.full_name?.[0] || 'U'}
                                  </div>
                                  <div className="flex-1">
                                    <div className="bg-muted rounded-lg p-2">
                                      <span className="font-semibold text-sm">{comment.author?.full_name}</span>
                                      <p className="text-sm whitespace-pre-wrap">{comment.text}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {user && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    placeholder="Write a comment..."
                                    value={commentText[post.id] || ''}
                                    onChange={(e) =>
                                      setCommentText((prev) => ({ ...prev, [post.id]: e.target.value }))
                                    }
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                                    className="flex-1 px-3 py-2 border rounded-lg text-sm bg-background"
                                  />
                                  <Button size="sm" onClick={() => handleAddComment(post.id)}>
                                    Post
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}

                  <div ref={observerTarget} className="h-10 flex items-center justify-center">
                    {loading && <div className="text-muted-foreground">Loading...</div>}
                  </div>
                </div>
              </>
            )}

            {/* POSTS TAB */}
            {activeTab === 'posts' && (
              <>
                {user ? (
                  <Card className="mb-4">
                    <CardContent className="p-4">
                      <Textarea
                        placeholder="What's on your mind?"
                        value={composerText}
                        onChange={(e) => setComposerText(e.target.value)}
                        rows={3}
                        className="mb-3"
                      />

                      {mediaImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {mediaImages.map((img, idx) => (
                            <div key={idx} className="relative">
                              <img src={img} className="w-full h-20 object-cover rounded" alt="" />
                              <button
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1"
                                aria-label="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            value={postType}
                            onChange={(e) => setPostType(e.target.value)}
                            className="px-3 py-1 border rounded text-sm bg-background"
                          >
                            <option value="PERSONAL">Personal</option>
                            <option value="WORK">Work</option>
                            <option value="HIRING">Hiring</option>
                            <option value="PROMOTION">Promotion</option>
                          </select>

                          {mediaImages.length < 4 && (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                                id="image-upload-posts"
                                disabled={uploading}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={uploading}
                                onClick={() => document.getElementById('image-upload-posts')?.click()}
                              >
                                <ImageIcon className="w-4 h-4 mr-1" />
                                {uploading ? 'Uploading...' : 'Add Photos'}
                              </Button>
                            </>
                          )}
                        </div>

                        <Button onClick={handleCreatePost} disabled={posting || uploading} size="sm">
                          {posting ? 'Posting...' : 'Post'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="mb-4">
                    <CardContent className="p-6 text-center">
                      <p className="text-muted-foreground mb-3">Join GoFella to share your story</p>
                      <Button onClick={() => navigate('/login')}>Login to Post</Button>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card key={post.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold overflow-hidden">
                            {post.author?.avatar_url ? (
                              <img src={post.author.avatar_url} className="w-full h-full object-cover" alt={post.author?.full_name} />
                            ) : (
                              post.author?.full_name?.[0] || 'U'
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => navigate(`/users/${post.author?.id || post.author_user_id}`)}
                                className="font-semibold hover:underline"
                              >
                                {post.author?.full_name || 'User'}
                              </button>
                              <Badge variant="outline" className="text-xs">
                                {post.post_type}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{new Date(post.created_at).toLocaleString()}</p>
                          </div>
                        </div>

                        <p className="text-foreground mb-3 whitespace-pre-wrap">{post.text}</p>

                        <div className="flex items-center gap-4 pt-3 border-t">
                          <button
                            onClick={() => handleLikePost(post.id)}
                            className={`flex items-center gap-1 ${
                              post.viewer_has_liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${post.viewer_has_liked ? 'fill-red-500' : ''}`} />
                            <span className="text-sm font-medium">{post.like_count || 0}</span>
                          </button>

                          <button
                            onClick={() => toggleComments(post.id)}
                            className="flex items-center gap-1 text-muted-foreground hover:text-blue-500"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm">{post.comment_count || 0}</span>
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  <div ref={observerTarget} className="h-10 flex items-center justify-center">
                    {loading && <div className="text-muted-foreground">Loading...</div>}
                  </div>
                </div>
              </>
            )}

            {/* NEWS TAB */}
            {activeTab === 'news' && (
              <div className="space-y-4">
                {pinnedNews.map((item) => (
                  <Card key={item.id} className="border-2 border-yellow-300">
                    <CardContent className="p-6">
                      <h2 className="text-2xl font-bold mb-2">{item.title}</h2>
                      <p className="text-sm text-muted-foreground mb-3">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                      <p className="whitespace-pre-wrap">{item.body}</p>
                    </CardContent>
                  </Card>
                ))}

                {regularNews.map((item) => (
                  <Card key={item.id}>
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold mb-2">{item.title}</h2>
                      <p className="text-sm text-muted-foreground mb-3">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                      <p className="whitespace-pre-wrap">{item.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Left Sidebar */}
          <div className="order-2 md:order-1 space-y-4">
            {user ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-3xl font-bold mb-3 overflow-hidden">
                      {user.avatar_url ? (
                        <img src={user.avatar_url} className="w-full h-full object-cover" alt={user.full_name} />
                      ) : (
                        (user.full_name || 'U')[0]
                      )}
                    </div>
                    <h3 className="font-bold text-center">{user.full_name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">{user.role}</p>

                    <div className="w-full mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Profile</span>
                        <span className="text-xs font-semibold">{user.avatar_url ? '75%' : '50%'}</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600" style={{ width: user.avatar_url ? '75%' : '50%' }} />
                      </div>
                      <button onClick={() => navigate('/settings')} className="text-xs text-blue-600 mt-1">
                        Complete profile
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/me/saved')}>
                      <Bookmark className="w-4 h-4 mr-2" />
                      Saved Items
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/messages')}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Messages
                    </Button>
                    <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => navigate('/settings')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-bold mb-2">Welcome to GoFella</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Your all-in-one platform for tradies, jobs, and talent
                  </p>
                  <div className="space-y-2">
                    <Button className="w-full" onClick={() => navigate('/register')}>
                      Sign Up Free
                    </Button>
                    <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                      Login
                    </Button>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-2" />
                      <p className="text-xs text-muted-foreground">Find verified tradies</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-2" />
                      <p className="text-xs text-muted-foreground">Discover job opportunities</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-blue-600 mt-2" />
                      <p className="text-xs text-muted-foreground">Connect with talent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="order-3 md:col-span-2 lg:col-span-1 space-y-4">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Spotlight
                </h3>
                <p className="text-sm text-muted-foreground">Featured services and opportunities</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Closing Soon
                </h3>

                {user && closingSoonJobs.length > 0 ? (
                  <div className="space-y-2">
                    {closingSoonJobs.map((job) => (
                      <div
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.job_code || job.id}`)}
                        className="p-2 border rounded hover:bg-muted cursor-pointer"
                      >
                        <h4 className="font-semibold text-sm line-clamp-1">{job.job_title || job.title}</h4>
                        <p className="text-xs text-muted-foreground">{job.company_name}</p>
                        <p className="text-xs text-orange-600 mt-1">
                          {job.days_left} day{job.days_left !== 1 ? 's' : ''} left
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No saved jobs expiring soon</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowSearch(true)} className="justify-start">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleShare} className="justify-start">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SearchDrawer isOpen={showSearch} onClose={() => setShowSearch(false)} userRole={user?.role} />
    </div>
  );
};

export default HomeFeedNews;
